// Same tolerance the lane lookup uses to merge near-identical directions.
// Suggestions are only kept 30° apart, so two can still both lie within it of
// one heading (e.g. 340° and 20° around 0°).
const HEADING_MATCH_TOLERANCE_DEG = 30

const angularDiff = (a: number, b: number) => {
  const d = Math.abs(a - b) % 360
  return Math.min(d, 360 - d)
}

/**
 * Whether two drag ticks offer the same directions, i.e. the marker is still on
 * the same lane. Bearings may drift a little along a curving lane; a crossing
 * street adds, drops or turns a direction past the tolerance.
 */
export const isSameSuggestionSet = (a: number[], b: number[]): boolean =>
  a.length === b.length && pairsOneToOne(a, b)

// Every bearing in `a` pairs with its own bearing in `b`, none reused. Tries
// each candidate in turn, since taking the closest first can block a pairing
// that exists. Lanes yield at most four directions, so this stays tiny.
const pairsOneToOne = (a: number[], b: number[]): boolean => {
  if (a.length === 0) return true
  const [first, ...rest] = a
  return b.some(
    (bearing, i) =>
      angularDiff(bearing, first) < HEADING_MATCH_TOLERANCE_DEG &&
      pairsOneToOne(
        rest,
        b.filter((_, j) => j !== i),
      ),
  )
}

/**
 * The suggested bearing that still matches `heading` while a waypoint is
 * dragged, or null once the suggestions no longer offer it (e.g. the marker
 * moved onto a crossing street). Returning the suggestion rather than the old
 * heading lets the kept heading follow a curving lane.
 */
export const matchingSuggestedBearing = (
  suggestions: number[],
  heading: number,
): number | null => {
  let best: number | null = null
  let bestDiff = HEADING_MATCH_TOLERANCE_DEG
  for (const bearing of suggestions) {
    const diff = angularDiff(bearing, heading)
    if (diff < bestDiff) {
      bestDiff = diff
      best = bearing
    }
  }
  return best
}
